import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Bot, Pause, Play, SendHorizontal, Square } from 'lucide-react';
import { toast } from 'sonner';
import {
  controlChatAgata,
  sendChatMessage,
  type AgataAction,
  type ChatDetail,
  type PauseAgataOnSend,
} from '@/entities/chats';
import { useAuth } from '@/features/auth';
import { ApiError } from '@/shared/api';
import { useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { Textarea } from '@/shared/ui/shadcn/textarea';

type AgataMode = 'active' | 'paused' | 'stopped' | 'escalated' | 'closed';

function agataMode(chat: ChatDetail, nowMs: number): AgataMode {
  if (chat.status === 'escalated') return 'escalated';
  if (chat.status === 'closed') return 'closed';
  if (chat.status === 'ai_active') return 'active';
  if (chat.status === 'operator_owned') {
    if (chat.agata_paused_until) {
      const until = Date.parse(chat.agata_paused_until);
      if (!Number.isNaN(until) && until > nowMs) return 'paused';
    }
    return 'stopped';
  }
  return 'stopped';
}

function formatRemaining(untilIso: string | undefined, nowMs: number, t: (m: number) => string) {
  if (!untilIso) return '';
  const until = Date.parse(untilIso);
  if (Number.isNaN(until) || until <= nowMs) return '';
  const mins = Math.max(1, Math.ceil((until - nowMs) / 60_000));
  return t(mins);
}

export function ClientChatComposer({ chat }: { chat: ChatDetail }) {
  const { t } = useI18n();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const canSend = hasPermission('can_send_messages');
  const isWhatsApp = chat.channel === 'whatsapp';
  const mode = useMemo(() => agataMode(chat, nowMs), [chat, nowMs]);

  useEffect(() => {
    if (mode !== 'paused') return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [mode]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['chat', chat.dialog_id] });
    await queryClient.invalidateQueries({ queryKey: ['chat-messages', chat.dialog_id] });
    await queryClient.invalidateQueries({ queryKey: ['chats'] });
  };

  const agata = useMutation({
    mutationFn: (action: AgataAction) => controlChatAgata(chat.dialog_id, action),
    onSuccess: async () => {
      toast.success(t.chats.agataUpdated);
      await invalidate();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.chats.agataFailed);
    },
  });

  const send = useMutation({
    mutationFn: (pause: PauseAgataOnSend) =>
      sendChatMessage(chat.dialog_id, {
        content: draft.trim(),
        pause_agata: pause,
      }),
    onSuccess: async () => {
      setDraft('');
      setConfirmOpen(false);
      toast.success(t.chats.sent);
      await invalidate();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.chats.sendFailed);
    },
  });

  const busy = send.isPending || agata.isPending;
  const remaining = formatRemaining(chat.agata_paused_until, nowMs, t.chats.agataPauseMinutes);

  const requestSend = () => {
    if (!draft.trim() || busy || !canSend || !isWhatsApp) return;
    if (mode === 'active') {
      setConfirmOpen(true);
      return;
    }
    send.mutate('none');
  };

  const statusLabel =
    mode === 'active'
      ? t.dialogStatus.ai_active
      : mode === 'paused'
        ? `${t.dialogStatus.operator_owned}${remaining ? ` · ${remaining}` : ''}`
        : mode === 'escalated'
          ? t.dialogStatus.escalated
          : mode === 'closed'
            ? t.dialogStatus.closed
            : t.chats.agataStopped;

  if (!canSend) {
    return null;
  }

  return (
    <div className="shrink-0 border-t bg-background p-3">
      {!isWhatsApp ? (
        <p className="mb-2 text-xs text-muted-foreground">{t.chats.sendWhatsAppOnly}</p>
      ) : null}
      <div className="flex items-end gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={busy || !isWhatsApp}
                aria-label={t.dialogStatus.label}
                className={cn(
                  mode === 'active' && 'border-emerald-600/40 text-emerald-700',
                  mode === 'paused' && 'border-amber-600/40 text-amber-700',
                  (mode === 'stopped' || mode === 'escalated' || mode === 'closed') &&
                    'border-muted-foreground/30 text-muted-foreground',
                )}
              />
            }
          >
            <Bot className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-56">
            <div className="px-2 py-1.5 text-xs text-muted-foreground">{statusLabel}</div>
            {mode === 'active' ? (
              <>
                <DropdownMenuItem
                  onClick={() => agata.mutate('pause_60m')}
                  className="gap-2"
                >
                  <Pause className="size-3.5" />
                  {t.chats.agataPause60}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => agata.mutate('stop')} className="gap-2">
                  <Square className="size-3.5" />
                  {t.chats.agataStop}
                </DropdownMenuItem>
              </>
            ) : null}
            {mode === 'paused' ? (
              <>
                <DropdownMenuItem onClick={() => agata.mutate('resume')} className="gap-2">
                  <Play className="size-3.5" />
                  {t.chats.agataResume}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => agata.mutate('stop')} className="gap-2">
                  <Square className="size-3.5" />
                  {t.chats.agataStop}
                </DropdownMenuItem>
              </>
            ) : null}
            {mode === 'stopped' ? (
              <DropdownMenuItem onClick={() => agata.mutate('resume')} className="gap-2">
                <Play className="size-3.5" />
                {t.chats.agataResume}
              </DropdownMenuItem>
            ) : null}
            {mode === 'escalated' ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                {t.chats.agataLocked}
              </div>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>

        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t.chats.messagePlaceholder}
          disabled={busy || !isWhatsApp}
          rows={1}
          className="min-h-10 max-h-32 flex-1 resize-none"
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              requestSend();
            }
          }}
        />
        <Button
          type="button"
          size="icon"
          disabled={busy || !isWhatsApp || draft.trim() === ''}
          aria-label={t.chats.send}
          onClick={requestSend}
        >
          <SendHorizontal className="size-4" />
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(open) => !send.isPending && setConfirmOpen(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.chats.pauseBeforeSendTitle}</DialogTitle>
            <DialogDescription>{t.chats.pauseBeforeSendHint}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="button"
              disabled={send.isPending}
              onClick={() => send.mutate('pause_60m')}
            >
              {t.chats.pauseAndSend}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={send.isPending}
              onClick={() => send.mutate('none')}
            >
              {t.chats.sendWithoutPause}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={send.isPending}
              onClick={() => setConfirmOpen(false)}
            >
              {t.crm.cancel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
